import React, { useState, useEffect } from 'react';
import { Input, Button } from 'antd';
import { SearchOutlined, SwapOutlined, ReloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Tabs } from 'atoms';
import RCTable from 'components/custom-table/RCTable';
import AddTeamMemberModal from './modals/AddTeamMemberModal';
import DeleteTeamMemberModal from './modals/DeleteTeamMemberModal';
import ReactivateTeamMemberModal from './modals/ReactivateTeamMemberModal';
import ChangeCityManagerModal from './modals/ChangeCityManagerModal';
import DeactivateBusinessUnitModal from './modals/DeactivateBusinessUnitModal';
import { getListAction } from 'store/pageSlice';
import { localProjectColumns } from '../../utils/table-config/BusinessUnitTableConfig';
import RemoveFillIcon, {
	ActiveStatusBadge,
	RemovedStatusBadge,
	ActionButtonContainer,
} from './RemoveFillIcon';

const SearchBarContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 201px;
	height: 37px;
	border-radius: 2px;
`;
const SearchInput = styled(Input)`
	width: 165px;
	height: 37px;
	background: #fff;
	border: 1px solid #c6c6c8;
	border-radius: 2px 0 0 2px;
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	font-size: 12px;
	line-height: 15px;
	color: #000;
	&::placeholder {
		color: #bfbfbf;
		font-size: 12px;
	}
`;
const SearchButton = styled(Button)`
	width: 37px;
	height: 37px;
	background: #fff;
	border: 1px solid #c6c6c8;
	border-left: none;
	border-radius: 0 2px 2px 0;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0;
`;

// eslint-disable-next-line react/no-multi-comp
export default function BusinessUnitTabs({ buId }) {
	const [activeTab, setActiveTab] = useState('team');
	const [searchValue, setSearchValue] = useState('');
	const [teamSearchStr, setTeamSearchStr] = useState('');
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [deletingMember, setDeletingMember] = useState(null);
	const [reactivatingMember, setReactivatingMember] = useState(null);
	const [changeCityManagerModalOpen, setChangeCityManagerModalOpen] = useState(false);
	const [cityManagerRow, setCityManagerRow] = useState(null);
	const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
	const [deactivateLoading, setDeactivateLoading] = useState(false);
	const [projectRows, setProjectRows] = useState({ rows: [], count: 0 });
	const dispatch = useDispatch();

	const [teamPaginateConfig, setTeamPaginateConfig] = useState({
		limit: 10,
		offset: 0,
		currentPage: 1,
	});
	const [teamRows, setTeamRows] = useState({ rows: [], count: 0 });

	const [projectPaginateConfig, setProjectPaginateConfig] = useState({
		limit: 10,
		offset: 0,
		currentPage: 1,
	});

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (activeTab === 'team' && buId) {
			dispatch(
				getListAction({
					search_type: 'bu_team_members',
					bu_id: buId,
					query_str: teamSearchStr,
					limit: teamPaginateConfig.limit,
					offset: teamPaginateConfig.offset,
				})
			).then((res) => {
				const rows = (res?.rows || []).map((item, idx) => ({
					key: item.id || idx,
					name: item.user_name,
					user_id: item.user_id,
					emp_id: item.employee_id,
					role: item.role,
					effectiveFrom: item.appointed_at.slice(0, 10),
					status: item.is_active ? 'Active' : 'Inactive',
					// eslint-disable-next-line no-nested-ternary
					endDate: item.is_active
						? 'Present'
						: item.removed_at
						? String(item.removed_at).slice(0, 10)
						: '',
				}));
				setTeamRows({ rows, count: res?.count || 0 });
			});
		}
	}, [activeTab, buId, dispatch, teamSearchStr, teamPaginateConfig]);

	useEffect(() => {
		if (activeTab === 'projects' && buId) {
			dispatch(
				getListAction({
					search_type: 'project',
					limit: projectPaginateConfig.limit,
					offset: projectPaginateConfig.offset,
					business_unit_id: buId,
				})
			).then((res) => {
				const rows = (res?.rows || []).map((item, idx) => ({
					key: item.id || idx,
					project_name: item.project_location_code || 'KJPV Project',
					client_name: item.client_name,
					created_at: item.created_at,
					id: item.id,
				}));
				setProjectRows({ rows, count: res?.count || 0 });
			});
		}
	}, [activeTab, buId, dispatch, projectPaginateConfig]);

	const teamColumns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Role',
			dataIndex: 'role',
			key: 'role',
		},
		{
			title: 'Start Date',
			dataIndex: 'effectiveFrom',
			key: 'effectiveFrom',
		},
		{
			title: 'End Date',
			dataIndex: 'endDate',
			key: 'endDate',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (val) => {
				if (val === 'Active') {
					return (
						<ActiveStatusBadge>
							<span>Active</span>
						</ActiveStatusBadge>
					);
				}

				return (
					<RemovedStatusBadge>
						<span>Removed</span>
					</RemovedStatusBadge>
				);
			},
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			render: (_, record) => {
				if (record.role === 'city_manager') {
					const isActive = record.status === 'Active';
					return (
						<ActionButtonContainer>
							<SwapOutlined
								style={{
									fontSize: 24,
									color: isActive ? '#143E60' : '#d9d9d9',
									width: 24,
									height: 24,
									position: 'relative',
									cursor: isActive ? 'pointer' : 'not-allowed',
									pointerEvents: isActive ? 'auto' : 'none',
								}}
								onClick={
									isActive
										? () => {
												setCityManagerRow(record);
												setChangeCityManagerModalOpen(true);
										  }
										: undefined
								}
							/>
						</ActionButtonContainer>
					);
				}
				if (record.status === 'Active') {
					return (
						<ActionButtonContainer>
							<RemoveFillIcon
								style={{
									width: 24,
									height: 24,
									position: 'relative',
									cursor: 'pointer',
								}}
								onClick={() => setDeletingMember(record)}
							/>
						</ActionButtonContainer>
					);
				}
				if (record.status === 'Removed' || record.status === 'Inactive') {
					return (
						<ActionButtonContainer>
							<ReloadOutlined
								style={{
									color: '#000',
									fontSize: 24,
									width: 24,
									height: 24,
									position: 'relative',
									cursor: 'pointer',
								}}
								onClick={() => setReactivatingMember(record)}
							/>
						</ActionButtonContainer>
					);
				}
				return null;
			},
		},
	];

	const refreshTeamList = () => {
		dispatch(
			getListAction({
				search_type: 'bu_team_members',
				bu_id: buId,
			})
		).then((res) => {
			const rows = (res?.rows || []).map((item, idx) => ({
				key: item.id || idx,
				name: item.user_name,
				user_id: item.user_id,
				role: item.role,
				effectiveFrom: item.appointed_at.slice(0, 10),
				status: item.is_active ? 'Active' : 'Inactive',
				// eslint-disable-next-line no-nested-ternary
				endDate: item.is_active
					? 'Present'
					: item.removed_at
					? String(item.removed_at).slice(0, 10)
					: '',
			}));
			setTeamRows(rows);
		});
	};

	const handleChangeCityManager = (newManager) => {
		const today = new Date().toISOString().slice(0, 10);
		setTeamRows((prev) => {
			const updatedRows = prev.map((row) => {
				if (cityManagerRow && row.key === cityManagerRow.key) {
					return {
						...row,
						status: 'Removed',
						endDate: today,
					};
				}
				return row;
			});
			return [
				...updatedRows,
				{
					key: Date.now(),
					name: newManager.name,
					role: 'city-manager',
					effectiveFrom: today,
					status: 'Active',
					endDate: '',
				},
			];
		});
		setChangeCityManagerModalOpen(false);
		setCityManagerRow(null);
	};

	const handleDeactivateBusinessUnit = async () => {
		setDeactivateLoading(true);
		await new Promise((resolve) => {
			setTimeout(resolve, 1000);
		});
		setDeactivateLoading(false);
		setDeactivateModalOpen(false);
	};

	return (
		<>
			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				items={[
					{ label: 'Team', key: 'team' },
					{ label: 'Projects', key: 'projects' },
				]}
			/>
			<div style={{ marginTop: 24 }}>
				{activeTab === 'team' && (
					<>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								marginBottom: 16,
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<span style={{ fontWeight: 600, fontSize: 16, color: '#000' }}>
									Manage Team
								</span>
								<SearchBarContainer>
									<SearchInput
										placeholder="Search Member"
										value={searchValue}
										onChange={(e) => setSearchValue(e.target.value)}
										onPressEnter={() => setTeamSearchStr(searchValue)}
									/>
									<SearchButton
										icon={
											<SearchOutlined
												style={{ color: '#ABABAC', fontSize: 20 }}
											/>
										}
										onClick={() => setTeamSearchStr(searchValue)}
									/>
								</SearchBarContainer>
							</div>
							<Button
								style={{
									background: '#18405D',
									color: '#fff',
									borderRadius: 4,
									width: 100,
								}}
								onClick={() => setAddModalOpen(true)}
							>
								ADD
							</Button>
						</div>
						<RCTable
							rowKey="key"
							tableDatas={[teamRows, setTeamRows]}
							togglePaginate={[teamPaginateConfig, setTeamPaginateConfig]}
							columns={teamColumns}
							hasPagination
						/>
						<AddTeamMemberModal
							open={addModalOpen}
							onCancel={() => setAddModalOpen(false)}
							teamRows={teamRows.rows}
							buId={buId}
							onAdd={() => {
								setAddModalOpen(false);
								setTeamSearchStr('');
							}}
						/>
						{buId != null && (
							<DeleteTeamMemberModal
								open={!!deletingMember}
								onCancel={() => setDeletingMember(null)}
								member={deletingMember}
								buId={buId}
								onDelete={async () => {
									await refreshTeamList();
									setDeletingMember(null);
								}}
							/>
						)}
						<ReactivateTeamMemberModal
							open={!!reactivatingMember}
							onCancel={() => setReactivatingMember(null)}
							member={reactivatingMember}
							buId={buId}
							onReactivate={async () => {
								await refreshTeamList();
								setReactivatingMember(null);
							}}
						/>
						{buId != null && (
							<ChangeCityManagerModal
								open={changeCityManagerModalOpen}
								onCancel={() => {
									setChangeCityManagerModalOpen(false);
									setCityManagerRow(null);
								}}
								onChange={handleChangeCityManager}
								teamRows={teamRows}
								currentManager={cityManagerRow}
								buId={buId}
								refreshTeam={() => {
									dispatch(
										getListAction({
											search_type: 'bu_team_members',
											bu_id: buId,
										})
									).then((res) => {
										const rows = (res?.rows || []).map((item, idx) => ({
											key: item.id || idx,
											name: item.user_name,
											role: item.role,
											user_id: item.user_id,
											effectiveFrom: item.appointed_at.slice(0, 10),
											status: item.is_active ? 'Active' : 'Inactive',
											// eslint-disable-next-line no-nested-ternary
											endDate: item.is_active
												? 'Present'
												: item.removed_at
												? String(item.removed_at).slice(0, 10)
												: '',
										}));
										setTeamRows(rows);
									});
								}}
							/>
						)}
					</>
				)}
				{activeTab === 'projects' && (
					<>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								marginBottom: 16,
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<span style={{ fontWeight: 600, fontSize: 16, color: '#000' }}>
									Add/Edit
								</span>
							</div>
							<Button
								style={{
									background: '#18405D',
									color: '#fff',
									borderRadius: 4,
									width: 100,
								}}
							>
								ADD
							</Button>
						</div>
						<RCTable
							rowKey="key"
							tableDatas={[projectRows, setProjectRows]}
							togglePaginate={[projectPaginateConfig, setProjectPaginateConfig]}
							columns={localProjectColumns}
							hasPagination
						/>
					</>
				)}
				<DeactivateBusinessUnitModal
					open={deactivateModalOpen}
					onCancel={() => setDeactivateModalOpen(false)}
					onDeactivate={handleDeactivateBusinessUnit}
					loading={deactivateLoading}
				/>
			</div>
		</>
	);
}

BusinessUnitTabs.propTypes = {
	buId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
